import { createContext, useContext, useSyncExternalStore } from "react";

import {
  createSelectionStore,
  type SelectionStore,
} from "src/pages/projects/components/attachment-selection-store";
import type { ProjectAttachment } from "src/pages/projects/project-attachments";

type AttachmentActions = {
  toggle: (linkId: string) => void;
  /** Selects every attachment the tab shows that can be selected, or none. */
  setAllSelected: (isSelected: boolean) => void;
  remove: (attachments: ProjectAttachment[]) => void;
  download: (attachments: ProjectAttachment[]) => void;
  /** Opens the choice of which other project to move them to. */
  moveToProject: (attachments: ProjectAttachment[]) => void;
  /** Opens the choice of which task to attach them to. */
  attachToTask: (attachments: ProjectAttachment[]) => void;
};

/** Both parts keep one identity for the tab's life, so consuming them never re-renders on its own. */
export type AttachmentActionsValue = {
  store: SelectionStore;
  actions: AttachmentActions;
};

export const AttachmentActionsContext =
  createContext<AttachmentActionsValue | null>(null);

// Stands in outside the tab, where nothing is ever selected.
const NO_SELECTION = createSelectionStore();

const useStore = () =>
  useContext(AttachmentActionsContext)?.store ?? NO_SELECTION;

/** The tab's actions; null on a page that only lists attachments. */
export const useAttachmentActions = (): AttachmentActions | null =>
  useContext(AttachmentActionsContext)?.actions ?? null;

/** Re-renders the caller only when this attachment's own selection flips. */
export function useIsAttachmentSelected(linkId: string): boolean {
  const store = useStore();
  return useSyncExternalStore(store.subscribe, () => store.has(linkId));
}

/** Whether anything is selected, so it changes only as the selection empties or first fills. */
export function useIsSelecting(): boolean {
  const store = useStore();
  return useSyncExternalStore(store.subscribe, () => store.getIds().size > 0);
}

export function useSelectedIds(): ReadonlySet<string> {
  const store = useStore();
  return useSyncExternalStore(store.subscribe, store.getIds);
}
